import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import { useAuth } from "../../js/hooks/useAuth";
import {
  getAdminUser,
  listAdminUsers,
  loginHistoryFor,
  resetUserPassword,
  setUserStatus,
  userDeskLinks,
} from "../../js/api/adminUsers";
import { formatDate } from "../../js/utils/formatters";
import { toTitleCase } from "../../js/utils/titleCase";

export default function AdminUsers() {
  const { session } = useAuth();
  const [pulse, setPulse] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [notice, setNotice] = useState("");

  const users = useMemo(() => listAdminUsers(), [pulse]);
  const selected = selectedId ? getAdminUser(selectedId) : users[0] || null;
  const history = useMemo(() => (selected ? loginHistoryFor(selected.id) : []), [selected, pulse]);
  const links = selected ? userDeskLinks(selected) : [];

  function refresh() {
    setPulse((n) => n + 1);
  }

  function toggleStatus() {
    if (!selected?.id) return;
    try {
      const next = selected.status === "inactive" ? "active" : "inactive";
      setUserStatus(selected.id, next, session?.userId);
      setNotice(next === "inactive" ? "Account disabled on this device." : "Account re-enabled.");
      setTempPassword("");
      refresh();
    } catch {
      setNotice("This account is not in the journey user table, so status cannot change here.");
    }
  }

  function resetPassword() {
    if (!selected?.id) return;
    const result = resetUserPassword(selected.id, session?.userId);
    setTempPassword(result.temporary_password);
    setNotice("A local temporary password was set. Nothing was emailed — this is a prototype only.");
    refresh();
  }

  return (
    <AppLayout variant="admin">
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <p className="page-lead">
            Everyone on this device. Password reset is local only. Billing and plans live in
            Account Management.
          </p>
        </div>
      </div>
      {notice ? <p className="confirm-banner">{notice}</p> : null}

      <div className="admin-split">
        <section className="desk-panel card">
          <h2>Directory</h2>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Beta</th>
                <th>Desk</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className={selected?.id === user.id ? "is-selected" : ""}
                  onClick={() => {
                    setSelectedId(user.id);
                    setTempPassword("");
                    setNotice("");
                  }}
                >
                  <td>{user.name || user.email}</td>
                  <td>{toTitleCase(user.role_type || user.role || "")}</td>
                  <td>{user.status || "active"}</td>
                  <td>{user.betaTester ? "Beta" : "—"}</td>
                  <td className="muted">
                    {user.role_type === "agent" || user.role_type === "broker" ? "Desk" : "Client"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="desk-panel card">
          {selected ? (
            <>
              <h2>{selected.name || "Profile"}</h2>
              <p className="muted">
                {selected.email} · {toTitleCase(selected.role_type || selected.role || "")} ·{" "}
                {selected.status || "active"}
              </p>
              {selected.office_name ? <p>Office {selected.office_name}</p> : null}
              {selected.betaTester ? (
                <p>
                  Beta tester · {toTitleCase(selected.betaRole || selected.role_type || "")}
                  {selected.betaAcceptedAt ? ` · accepted ${formatDate(selected.betaAcceptedAt)}` : ""}
                </p>
              ) : null}
              <p className="muted">
                Plans and invoices live in{" "}
                <Link to={`/admin/accounts/${selected.id}`}>Account Management</Link>.
              </p>
              <div className="row admin-user-links">
                {links.map((link) => (
                  <Link key={link.to} className="ghost-btn" to={link.to}>
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="row" style={{ gap: "0.65rem", marginTop: "0.85rem" }}>
                <button type="button" className="ghost-btn" onClick={toggleStatus}>
                  {selected.status === "inactive" ? "Re-enable" : "Disable"}
                </button>
                <button type="button" className="primary-btn" onClick={resetPassword}>
                  Reset password
                </button>
              </div>
              {tempPassword ? (
                <p className="confirm-banner">
                  Temporary password: <strong>{tempPassword}</strong>
                </p>
              ) : null}

              <h3>Login history</h3>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Role</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDate(row.at)}</td>
                      <td>{toTitleCase(row.role)}</td>
                      <td>{row.email}</td>
                    </tr>
                  ))}
                  {!history.length ? (
                    <tr>
                      <td colSpan={3} className="muted">
                        No sign-ins recorded yet on this device.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </>
          ) : (
            <p className="muted">No users on this device yet.</p>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
